// frontend/src/components/UI/DropdownMenu.jsx
// Componente reutilizável de menu dropdown

import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Componente de Menu Dropdown reutilizável
 * 
 * @param {React.ReactNode} trigger - Elemento que dispara o dropdown
 * @param {array} items - Array de itens do menu [{ id, label, icon, image, imageAlt, onClick, ... }]
 * @param {string} position - Posição do dropdown ('bottom-right', 'bottom-left', 'top-right', 'top-left')
 * @param {boolean} isOpen - Controla se o dropdown está aberto (controlado)
 * @param {function} onOpenChange - Callback quando o estado muda (para modo controlado)
 * @param {string} className - Classes adicionais para o container
 */
const DropdownMenu = ({ 
    trigger, 
    items = [], 
    position = 'bottom-right',
    isOpen: controlledIsOpen,
    onOpenChange,
    className = ''
}) => {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);

    // Usa estado controlado se fornecido, senão usa interno
    const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
    const setIsOpen = useCallback((value) => {
        if (controlledIsOpen === undefined) {
            setInternalIsOpen(value);
        }
        if (onOpenChange) {
            onOpenChange(value);
        }
    }, [controlledIsOpen, onOpenChange]);

    // Fechar ao clicar fora
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event) => {
            if (
                dropdownRef.current && 
                triggerRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !triggerRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isOpen, setIsOpen]);

    // Fechar ao pressionar Escape
    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, setIsOpen]);

    // Classes de posicionamento
    const positionClasses = {
        'bottom-right': 'top-full right-0 mt-2',
        'bottom-left': 'top-full left-0 mt-2',
        'top-right': 'bottom-full right-0 mb-2',
        'top-left': 'bottom-full left-0 mb-2'
    };

    const handleItemClick = (item) => {
        if (item.onClick) {
            item.onClick(item);
        }
        setIsOpen(false);
    };

    // Componente interno para cada item do dropdown (permite usar hooks)
    const DropdownMenuItem = ({ item, onItemClick }) => {
        const [imageError, setImageError] = useState(false);
        const Icon = item.icon;

        return (
            <button
                onClick={() => onItemClick(item)}
                className={`
                    w-full flex items-center gap-3 px-4 py-3
                    hover:bg-gray-50 rounded-lg transition
                    text-left
                    ${item.className || ''}
                `}
                disabled={item.disabled}
            >
                {/* Prioriza imagem se disponível, senão usa ícone */}
                {item.image && !imageError ? (
                    <img
                        src={item.image}
                        alt={item.imageAlt || item.label || ''}
                        className="h-8 w-8 rounded-full object-cover border border-gray-200 flex-shrink-0"
                        onError={() => {
                            setImageError(true);
                        }}
                    />
                ) : Icon ? (
                    <div className="h-8 w-8 flex items-center justify-center flex-shrink-0">
                        <Icon 
                            size={28} 
                            className={item.iconColor || 'text-gray-600'} 
                        />
                    </div>
                ) : null}
                <span className={`text-sm font-medium ${item.textColor || 'text-gray-700'}`}>
                    {item.label}
                </span>
            </button>
        );
    };

    return (
        <div className={`relative inline-block ${className}`}>
            {/* Trigger */}
            <div
                ref={triggerRef}
                onClick={() => setIsOpen(!isOpen)}
                className="cursor-pointer"
            >
                {trigger}
            </div>

            {/* Dropdown Menu */}
            {isOpen && (
                <div
                    ref={dropdownRef}
                    className={`
                        absolute
                        ${positionClasses[position]}
                        z-[9999] min-w-[200px]
                        bg-white rounded-2xl shadow-2xl border border-gray-200
                        py-2 animate-fadeIn
                    `}
                >
                    {items.map((item) => (
                        <DropdownMenuItem 
                            key={item.id} 
                            item={item} 
                            onItemClick={handleItemClick} 
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default DropdownMenu;

